import { ThunkResult } from "common/reducers";
import { RootDispatchType, RootGetState } from "common/store";
import { EstimateEditorActions } from "common/reducers/estimateEditor.actions";
import { MeasurementActions } from "common/reducers/measurement.actions";
import { estimate as estimateSelector, currentEstimateId } from "common/selectors/estimate.selectors";
import { estimateGroup as estimateGroupSelector } from "common/selectors/estimateGroup.selectors";
import {
  estimateLineItem as estimateLineItemSelector,
  unsavedId as estimateLineItemUnsavedIdSelector,
  duplicateSourceToCloneIdMap,
} from "common/selectors/estimateLineItem.selectors";
import { productOverviewById, productById } from "common/selectors/product.selectors";
import { validateLineItem } from "common/helpers/estimatorValidation";
import { compareLineItemOptions } from "common/helpers/ProductLineItem";
import { IOpeningGroup } from "common/helpers/OpeningsWizard";
import { ValidationKind } from "common/records/Validation";
import { Nullable } from "common/types";
import { EstimateLineItemRecord, ValidationResultRecord } from "common/records/EstimateLineItem";
import { ProductRecord } from "common/records/Product";
import { resolveFiles } from "common/helpers/resolveFiles";
import { document as documentSelector } from "common/selectors/document.selectors";
import { DocumentActions } from "common/reducers/document.actions";
import { WARNING, addFlashAlert } from "common/components/FlashAlert";
import { List, Map } from "immutable";

export const EstimateEditorAsyncActions = {
  /**
   * Single chokepoint for the follow-up side effects every estimate mutation must trigger:
   * recompute totals, then re-run client-side validations. Routing all mutations through this one
   * thunk — rather than dispatching checkValidations from each UI handler — guarantees a new
   * mutation entry point (or a future feature like inspections) can't silently skip validation.
   * recalculateEstimateTotals is a pure recompute from byId and does not touch the saved
   * baseline, so it is idempotent and safe to run on delete/toggle/drag/save/apply.
   *
   * @param estimateId The id of the estimate that was just mutated
   */
  afterEstimateMutation:
    (estimateId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.recalculateEstimateTotals(estimateId));
      dispatch(EstimateEditorAsyncActions.checkValidations(estimateId));
    },

  /**
   * Follow-up side effect for a markup change: recompute totals — re-multiplying every markupable
   * line item by the new estimate-level markup — and re-run validations. Invoked from a 500ms
   * `useMemo`'d debounce in `Markups.tsx`, driven by the `onChange` of both the markup slider and the
   * number box, so rapid slider drags / typing coalesce into a single recompute; the debounce is
   * cancelled on unmount so a trailing tick can't dispatch against a torn-down estimate. The markup
   * value itself is written to the form slice on every change by the Markups form's `updateFormReducer`
   * (so the estimate is dirty immediately and the value is already in `byId` before this runs); this
   * thunk carries only the recalc/revalidate. It replaces the removed `UPDATE_ESTIMATE_MARKUP` sync
   * action, which summed group totals without re-multiplying line items.
   *
   * @param estimateId The id of the estimate whose markup changed
   */
  updateEstimateMarkup:
    (estimateId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Re-creates each source line item's OWNED documents on its clone line item after an estimate-level
   * duplicate (BR-9). Owned IMAGES are handled synchronously in the DUPLICATE_ESTIMATE_LINE_ITEM reducer
   * (cloned onto negative ids and copied server-side via `remote_file_url` on save — item 4); this thunk
   * covers DOCUMENTS ONLY. Documents can't ride the shared save transform without touching template
   * apply / ProVia imports, so they re-upload client-side: `resolveFiles` reads the source doc's bytes
   * (staged `file_queue` or fetched blob) and each File is staged as a fresh negative-id `file_queue`
   * record on the clone — the subsequent `saveEstimate` uploads them through the S3-direct `batchCreate`
   * path. Any document that can't be fetched (resolveFiles drops it) is skipped and surfaced with a
   * single warning flash after the whole cascade; it never blocks the rest of the duplicate. Reads the
   * source→clone map the duplicate cascade emitted, so it needs no estimate id.
   */
  duplicateEstimateAttachments: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const map = duplicateSourceToCloneIdMap(getState());
      let anyUnresolved = false;
      for (const [sourceLineItemId, cloneLineItemId] of map.entries()) {
        const sourceLineItem = estimateLineItemSelector(getState(), { estimateLineItemId: sourceLineItemId });
        if (!sourceLineItem) continue;

        const ownedDocumentIds = (sourceLineItem.document_ids ?? List<number>()).filter((id) => id > 0);

        const documentFiles = await resolveFiles(getState(), ownedDocumentIds, "document");

        // resolveFiles silently drops any document it can't read; a shorter result means one or more
        // failed to fetch. Re-stage every file that resolved and flag the miss — warned once below
        // (not once per line item) so a multi-line-item duplicate can't spam identical toasts.
        if (documentFiles.length < ownedDocumentIds.size) {
          anyUnresolved = true;
        }

        documentFiles.forEach(({ file, sourceId }) => {
          // Preserve the source document's display (proposal/agreement/both/hidden) — re-staging every
          // clone as "both" would silently surface a contract-only or do-not-display doc in the copy's
          // proposal. Fall back to "both" only if the source record vanished between resolve and re-stage.
          const display = documentSelector(getState(), { documentId: sourceId })?.display ?? "both";
          dispatch(
            DocumentActions.estimateLineItemDocument(cloneLineItemId, {
              file_queue: file,
              name: file.name,
              content_type: file.type || "application/pdf",
              display,
              documentable_id: cloneLineItemId,
              documentable_type: "EstimateLineItem",
            }),
          );
        });
      }
      if (anyUnresolved) {
        addFlashAlert(WARNING, "Some attachments could not be copied to the duplicate.");
      }
    };
  },

  /**
   * Confirm-and-swap entry point for changing a line item's product to a sibling variant. Both swap
   * paths — the inline row dropdown (EstimateLineItem) and the ProductSelector drill-down — route
   * through here so they behave identically. The caller decides (via the two confirm prompts, see
   * `useProductSwap`) whether to keep a user-edited description/quantity; this thunk applies the swap
   * with those flags, then runs the mandatory follow-up (recompute totals + revalidate) through the
   * afterEstimateMutation chokepoint. MUST NOT import estimate.async.actions (known cycle:
   * estimate.async → estimateEditor.async).
   *
   * @param lineItemId The line item to swap
   * @param productId The sibling variant product to swap in
   * @param options keepDescription / keepQuantity — preserve the user's edits when true
   */
  swapEstimateLineItemProduct:
    (
      lineItemId: number,
      productId: number,
      options: { keepDescription: boolean; keepQuantity: boolean },
    ): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(EstimateEditorActions.setEstimateLineItemProduct(lineItemId, productId, options));
      // Re-read post-swap and resolve the estimate id off the line item's group for the follow-up.
      const lineItem = estimateLineItemSelector(getState(), { estimateLineItemId: lineItemId });
      const group = lineItem
        ? estimateGroupSelector(getState(), { estimateGroupId: lineItem.estimate_group_id })
        : null;
      const estimateId = group?.estimate_id;
      if (estimateId) {
        dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
      }
    },

  saveLineItemSession:
    (estimateId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.commitLineItemSession());
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Cancels an open line-item editing session — the reducer restores an edited existing item from its
   * snapshot and removes a create-mode draft — then runs the mandatory follow-up (recompute totals +
   * revalidate) through the afterEstimateMutation chokepoint. CANCEL_LINE_ITEM_SESSION no longer
   * recomputes totals in-reducer; routing cancel through here (mirroring deleteLineItem /
   * saveLineItemSession) is what keeps validations from going stale when a session is cancelled after a
   * sibling line item was deleted mid-edit. Resolves the estimate id from the store when the caller
   * passes null, so a call site holding the `number | null` currentEstimateId selection dispatches
   * unconditionally. MUST NOT import estimate.async.actions (known cycle: estimate.async →
   * estimateEditor.async).
   *
   * @param estimateId The estimate being edited, or null to resolve it from currentEstimateId
   * @param lineItemId The line item whose session is cancelled (a create-mode draft when negative)
   */
  cancelLineItemSession:
    (estimateId: number | null, lineItemId?: number): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      const id = estimateId ?? currentEstimateId(getState());
      dispatch(EstimateEditorActions.cancelLineItemSession(lineItemId));
      if (id != null) dispatch(EstimateEditorAsyncActions.afterEstimateMutation(id));
    },

  deleteLineItem:
    (estimateId: number, estimateGroupId: number, lineItemId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.removeEstimateLineItem(estimateGroupId, lineItemId));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  deleteLineItemFromEditor:
    (estimateId: number, estimateGroupId: number, lineItemId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.removeEstimateLineItem(estimateGroupId, lineItemId));
      dispatch(EstimateEditorActions.commitLineItemSession());
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  lineItemDragEnd:
    (
      estimateId: number,
      sourceGroupId: number,
      destGroupId: number,
      oldVisibleIndex: number,
      newVisibleIndex: number,
    ): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.lineItemDragEnd(sourceGroupId, destGroupId, oldVisibleIndex, newVisibleIndex));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  toggleGroupIncluded:
    (estimateId: number, groupId: number, newIncluded: boolean): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(
        EstimateEditorActions.updateEstimateGroup({
          rootPath: ["estimateGroups", "byId", groupId],
          name: "included",
          value: newIncluded,
          editContext: { estimateGroupId: groupId },
        }),
      );
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  deleteGroup:
    (estimateId: number, groupId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.removeEstimateGroup(estimateId, groupId));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Window Estimator (Openings Wizard) — creates a line item from a Step-1 size group and lands the
   * user in the Step-2 editor. Ports rsf-app OpeningsWizard.addProducts, honoring its critical
   * ordering: the openings are attached to the line item BEFORE the default options are (re)applied,
   * so per_ui/per_sqft option quantities scale by the opening's UI/area. ADD_ESTIMATE_LINE_ITEM
   * seeds the product's defaults at creation with no openings attached (unscaled), so this thunk
   * attaches the openings and then re-applies the defaults via SET_ESTIMATE_LINE_ITEM_PRODUCT to
   * pick up the scaling, then applies the size-based window price/name adjustment. The created line
   * item's id is deterministic (the current unsaved id, which ADD assigns and decrements), so the
   * caller reads it from the store before dispatching.
   *
   * @param estimateId The estimate being edited
   * @param estimateGroupId The estimate group the line item is added to
   * @param group The Step-1 size group (bracket product, its openings, and quantity)
   */
  addWizardProducts:
    (estimateId: number, estimateGroupId: number, group: IOpeningGroup): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      // Open the per-group editing session, then mint the draft and mark it explicitly so Cancel and
      // the header-X abort remove it (draft-ness is never inferred from snapshot absence). The scoped
      // snapshot never captures this draft, so the explicit mark is what makes it removable.
      dispatch(EstimateEditorActions.startLineItemSession(estimateId));

      const newLineItemId = estimateLineItemUnsavedIdSelector(getState());

      dispatch(EstimateEditorActions.addEstimateLineItem(estimateGroupId, group.product.id));
      dispatch(EstimateEditorActions.markLineItemDraft(newLineItemId));

      // Attach the selected openings, then re-run the product application so default option
      // quantities scale by the now-attached opening (rsf-app attaches openings before
      // addDefaultOptions). In manual mode the group has no openings and this is a no-op reconcile.
      if (!group.openings.isEmpty()) {
        dispatch(EstimateEditorActions.setLineItemOpeningEstimations(newLineItemId, group.openings));
        dispatch(EstimateEditorActions.setEstimateLineItemProduct(newLineItemId, group.product.id));
      }

      // Manual mode drives quantity from the sibling row; automatic mode uses the opening count.
      const quantity = group.openings.isEmpty() ? group.quantity : group.openings.size;
      dispatch(EstimateEditorActions.updateEstimateLineItemField(newLineItemId, "quantity", quantity));

      dispatch(EstimateEditorActions.applyWindowPriceAdjustment(newLineItemId, group.product.id));

      // Auto-apply any saved estimate defaults to this group's line item (rsf-app group-open flow
      // runs updateLineItemOptions after addDefaultOptions) — so once the user clicks "Set Options"
      // on one group, later groups pick them up without a manual "Use Options" each time. Safe to
      // dispatch unconditionally: APPLY_ESTIMATE_LINE_ITEM_DEFAULTS early-returns when there are no
      // saved defaults, leaving the product's own just-applied defaults intact.
      dispatch(EstimateEditorActions.applyEstimateLineItemDefaults(estimateId, newLineItemId));

      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Window Estimator — switches the wizard's line item to a different parent-bracket product
   * (rsf-app confirmedChangeProduct). Carries over the options that survive the comparison with
   * their preserved quantities, then re-applies the size-based window price adjustment for the new
   * product. The caller has already resolved (and, if options would be lost, confirmed) the switch
   * via `compareLineItemOptions`.
   *
   * @param estimateId The estimate being edited
   * @param lineItemId The wizard's line item
   * @param newProduct The bracket product to switch to
   */
  changeWizardProduct:
    (estimateId: number, lineItemId: number, newProduct: ProductRecord): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      const { matched } = compareLineItemOptions(getState(), lineItemId, newProduct);
      dispatch(EstimateEditorActions.changeLineItemProductWithOptions(lineItemId, newProduct.id, matched));
      dispatch(EstimateEditorActions.applyWindowPriceAdjustment(lineItemId, newProduct.id));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Window Estimator — applies an edited opening selection to the line item (rsf-app
   * updateOpeningsSelection): reconciles the opening estimations to the given openings, sets the
   * quantity to the live opening count, and re-applies the size-based window price adjustment.
   *
   * @param estimateId The estimate being edited
   * @param lineItemId The wizard's line item
   * @param openings The openings now selected for the line item
   */
  updateWizardOpenings:
    (estimateId: number, lineItemId: number, openings: IOpeningGroup["openings"]): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(EstimateEditorActions.setLineItemOpeningEstimations(lineItemId, openings));
      dispatch(EstimateEditorActions.updateEstimateLineItemField(lineItemId, "quantity", openings.size));

      const lineItem = estimateLineItemSelector(getState(), { estimateLineItemId: lineItemId });
      const product =
        lineItem?.product_id != null
          ? (productOverviewById(getState(), lineItem.product_id) ?? productById(getState(), lineItem.product_id))
          : null;
      if (product) {
        dispatch(EstimateEditorActions.applyWindowPriceAdjustment(lineItemId, product.id));
      }
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Window Estimator — closes the wizard via the header X (or Step-0 Cancel), rolling back anything
   * in flight. On Step 3, unsaved measurement additions/edits are discarded first
   * (`resetMeasurement`). Then, if a line-item editing session is open, it is cancelled — removing an
   * in-progress create-mode draft (marked explicitly) and restoring an edit-existing line item from
   * its snapshot. When no session is open (e.g. Step 0 before any group was added) this is a no-op,
   * matching today's plain-close behavior.
   *
   * @param estimateId The estimate being edited
   * @param lineItemId The wizard's in-progress line item, if any
   * @param discardMeasurementEdits Whether to reset unsaved measurement edits (true on Step 3)
   */
  abortOpeningsWizard:
    (estimateId: number, lineItemId: Nullable<number>, discardMeasurementEdits: boolean): ThunkResult<void> =>
    (dispatch: RootDispatchType, getState: RootGetState) => {
      if (discardMeasurementEdits) {
        dispatch(MeasurementActions.resetMeasurement());
      }
      // A session is active when a line item is being edited (its per-item snapshot is present) or a
      // create draft exists in the store.
      const lineItemSnapshots = getState().getIn(["estimateLineItems", "editingSnapshotById"]) as Map<number, unknown>;
      const draftExists =
        lineItemId != null && !!estimateLineItemSelector(getState(), { estimateLineItemId: lineItemId });
      if (lineItemSnapshots.size > 0 || draftExists) {
        dispatch(EstimateEditorAsyncActions.cancelLineItemSession(estimateId, lineItemId ?? undefined));
      }
    },

  /**
   * Window Estimator — updates a single wizard line-item field, then re-runs the mandatory follow-up
   * (recalc totals + validations) through afterEstimateMutation. Bundling the two so every
   * price-affecting edit (product/labor price, markup, quantity) recomputes in one place rather than
   * each call site chaining the pair. The Description field intentionally does NOT use this — it has
   * no pricing effect and skips the recompute (bare updateEstimateLineItemField).
   *
   * @param estimateId The estimate being edited
   * @param lineItemId The wizard's line item
   * @param field The line-item field to set
   * @param value The new value
   */
  updateWizardLineItemField:
    (estimateId: number, lineItemId: number, field: string, value: string | number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.updateEstimateLineItemField(lineItemId, field, value));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
    },

  /**
   * Window Estimator — "Set Options": saves the current line item's options as the estimate's
   * default options (rsf-app setDefaultOptions) and flashes success.
   */
  setWizardDefaultOptions:
    (estimateId: number, lineItemId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.updateEstimateLineItemDefaults(estimateId, lineItemId));
      addFlashAlert("success", "Successfully set Estimate Default Options");
    },

  /**
   * Window Estimator — "Use Options": applies the estimate's saved default options to the current
   * line item (rsf-app useDefaultOptions) and flashes success.
   */
  applyWizardDefaultOptions:
    (estimateId: number, lineItemId: number): ThunkResult<void> =>
    (dispatch: RootDispatchType) => {
      dispatch(EstimateEditorActions.applyEstimateLineItemDefaults(estimateId, lineItemId));
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));
      addFlashAlert("success", "Successfully used Estimate Default Options");
    },

  /**
   * Runs client-side validation across all included line items for the given estimate
   * and dispatches a single PATCH_LINE_ITEM_VALIDATIONS action with results.
   */
  checkValidations: (estimateId: number): ThunkResult<void> => {
    return (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();
      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) return;

      const estimateKind = (estimateRecord.kind ?? "default") as ValidationKind;
      const activatedPriceListId = estimateRecord.activated_price_list_id;
      if (!activatedPriceListId) return;

      // Collect all included line items (for companion product checks)
      const allIncludedLineItems: EstimateLineItemRecord[] = [];
      for (const groupId of estimateRecord.group_ids) {
        const group = estimateGroupSelector(state, { estimateGroupId: groupId });
        if (!group || !group.included || group._destroy) continue;
        for (const lineItemId of group.line_item_ids) {
          const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
          if (lineItem && !lineItem._destroy) {
            allIncludedLineItems.push(lineItem);
          }
        }
      }

      const patches: Array<{ id: number; validated: boolean; validation_results: List<ValidationResultRecord> }> = [];

      for (const groupId of estimateRecord.group_ids) {
        const group = estimateGroupSelector(state, { estimateGroupId: groupId });
        if (!group || group._destroy) continue;

        for (const lineItemId of group.line_item_ids) {
          const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
          if (!lineItem || lineItem._destroy || !lineItem.product_id) continue;

          if (!group.included) {
            // Excluded groups always pass
            if (!lineItem.validated || lineItem.validation_results.size > 0) {
              patches.push({ id: lineItemId, validated: true, validation_results: List<ValidationResultRecord>() });
            }
            continue;
          }

          const result = validateLineItem(state, lineItem, estimateKind, activatedPriceListId, allIncludedLineItems);
          patches.push({
            id: lineItemId,
            validated: result.validated,
            validation_results: List(result.validation_results),
          });
        }
      }

      if (patches.length > 0) {
        dispatch(EstimateEditorActions.patchLineItemValidations(patches));
      }
    };
  },
};
